import { useEffect, useId, useRef, useState } from "react";
import type { MessageKey } from "@gioia365/i18n";
import { Button } from "../../design/components/Button";
import "../../design/components/text-field.css";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { withoutControlCharacters } from "../lead-display";
import { focusStayedIn } from "./focus";

// Il riquadro di una nota da scrivere (piano M2 Task 19, mock-up `scheda-lead.html`): «Nuova nota» in cima alla Timeline
// e «Appunto su questa chiamata» sotto una telefonata. Una nota salvata non si modifica né si cancella: il riquadro lo
// dice, e dopo il salvataggio si svuota. Il testo resta finché il server non risponde, così un errore non lo perde; in
// quel tempo il riquadro è di sola lettura, perché ciò che si scrivesse andrebbe perso con lo svuotamento. Il pulsante
// non si spegne mai del tutto (`aria-disabled`): il focus resta lì, e dopo il salvataggio torna nel riquadro, a meno che
// nel frattempo la persona non sia andata altrove.

/** Il limite del testo di una nota (`addNoteBodySchema`). */
const NOTE_MAX_LENGTH = 10_000;

type Texts = { label: MessageKey; submit: MessageKey; saving: MessageKey; required: MessageKey; placeholder?: MessageKey; hint?: MessageKey };

type Props = {
  texts: Texts;
  /** Salva la nota; un errore resta scritto sotto il riquadro. */
  onSave: (body: string) => Promise<unknown>;
  /** «Annulla», per il riquadro che si apre a richiesta (l'appunto). */
  onCancel?: () => void;
  id?: string;
  /** Il cursore nel riquadro appena si apre (l'appunto, aperto a richiesta). */
  focusOnOpen?: boolean;
  className?: string;
};

export function NoteComposer({ texts, onSave, onCancel, id, focusOnOpen = false, className }: Props) {
  const t = useT();
  const generatedId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fieldId = id ?? generatedId;
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Due clic di fila mandano una nota sola, anche prima che lo stato si aggiorni.
  const inFlight = useRef(false);

  useEffect(() => {
    if (focusOnOpen) textRef.current?.focus();
  }, [focusOnOpen]);

  async function save() {
    if (inFlight.current) return;
    const body = withoutControlCharacters(text).trim();
    if (body === "") {
      setError(t(texts.required));
      textRef.current?.focus();
      return;
    }
    setError(null);
    inFlight.current = true;
    setSaving(true);
    try {
      await onSave(body);
      setText("");
      // Pronto per la nota dopo, se la persona è rimasta qui (il riquadro di un appunto si chiude, e il focus lo riporta
      // chi lo ha aperto); se intanto scrive altrove, il cursore resta suo.
      if (focusStayedIn(rootRef.current)) textRef.current?.focus();
    } catch (cause) {
      setError(messageForError(t, cause));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const describedBy = [error ? errorId : null, texts.hint ? hintId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div ref={rootRef} className={className ? `g-note-composer ${className}` : "g-note-composer"}>
      <label className="g-field__label" htmlFor={fieldId}>
        {t(texts.label)}
      </label>
      <textarea
        ref={textRef}
        id={fieldId}
        className="g-field__input g-note-composer__text"
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        value={text}
        readOnly={saving}
        placeholder={texts.placeholder ? t(texts.placeholder) : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        onChange={(event) => {
          setText(event.target.value);
          if (error) setError(null);
        }}
      />
      {error ? (
        <p className="g-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      <div className="g-note-composer__row">
        {texts.hint ? (
          <span className="g-note-composer__hint" id={hintId}>
            {t(texts.hint)}
          </span>
        ) : null}
        <span className="g-note-composer__actions">
          {onCancel ? <Button onClick={onCancel}>{t("common.cancel")}</Button> : null}
          <Button variant={onCancel ? "primary" : "secondary"} aria-disabled={saving ? "true" : undefined} onClick={() => void save()}>
            {saving ? t(texts.saving) : t(texts.submit)}
          </Button>
        </span>
      </div>
    </div>
  );
}
